import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api/admin';

// ─── Theme (matches App.js dark theme) ───────────────────
const T = {
  bg: '#0f1117', card: '#181b27', border: '#1e2130', headerBg: '#13151e',
  text: '#e1e4ea', muted: '#8b8fa3', dim: '#4a5568',
  green: '#22c55e', red: '#ef4444', blue: '#3b82f6', yellow: '#fbbf24', orange: '#f97316',
  input: '#1a1d2a', inputBorder: '#2a2d3a',
};

const S = {
  label: { fontSize: 11, color: T.muted, marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '8px 10px', fontSize: 12, background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 5, color: T.text, outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { padding: '8px 20px', fontSize: 12, fontWeight: 600, background: T.blue, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' },
  btnDanger: { padding: '6px 14px', fontSize: 11, fontWeight: 500, background: 'transparent', color: T.red, border: `1px solid #4a2020`, borderRadius: 4, cursor: 'pointer' },
  btnSmall: { padding: '5px 12px', fontSize: 10, fontWeight: 500, background: 'transparent', color: T.muted, border: `1px solid ${T.inputBorder}`, borderRadius: 4, cursor: 'pointer' },
};

// ═══════════════════════════════════════════════════════════
// PASSWORD STRENGTH INDICATOR (Guardrail 3)
// ═══════════════════════════════════════════════════════════
function PasswordStrength({ password }) {
  if (!password) return null;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^a-zA-Z0-9]/.test(password);
  const hasLength = password.length >= 6;
  const checks = [
    { label: '6+ characters', ok: hasLength },
    { label: '1 letter', ok: hasLetter },
    { label: '1 number', ok: hasNumber },
    { label: '1 special char', ok: hasSpecial },
  ];
  const allOk = checks.every(c => c.ok);
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
      {checks.map(c => (
        <span key={c.label} style={{ fontSize: 10, color: c.ok ? T.green : T.red }}>
          {c.ok ? '✓' : '✗'} {c.label}
        </span>
      ))}
    </div>
  );
}

function isPasswordStrong(password) {
  return password && password.length >= 6 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password) && /[^a-zA-Z0-9]/.test(password);
}

// ═══════════════════════════════════════════════════════════
// CONFIRMATION POPUP — used for all risky actions
// ═══════════════════════════════════════════════════════════
function ConfirmPopup({ title, message, confirmLabel, confirmColor, onConfirm, onCancel, children }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onCancel}>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, width: 380, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 16, lineHeight: 1.5 }}>{message}</div>
        {children}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={{ ...S.btnSmall, padding: '8px 20px', fontSize: 12 }}>Cancel</button>
          <button onClick={onConfirm} style={{ ...S.btnPrimary, background: confirmColor || T.red }}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN ADMIN PANEL
// ═══════════════════════════════════════════════════════════
export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username: '', displayName: '', role: 'trader', canLoginBroker: false, canLogoutBroker: false });
  const [createAccounts, setCreateAccounts] = useState([]); // for trader account selection at creation
  const [createError, setCreateError] = useState('');

  // Edit user
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Account access editor (traders only now)
  const [accessUser, setAccessUser] = useState(null);
  const [accessState, setAccessState] = useState([]);

  // Confirmation popup
  const [confirm, setConfirm] = useState(null);

  // ─── Fetch users ────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      setError('');
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.users);
      setAvailableAccounts(res.data.availableAccounts);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  // ─── Open create popup — initialize account list ────────
  function openCreate() {
    setShowCreate(true);
    setCreateError('');
    setForm({ username: '', displayName: '', role: 'trader', canLoginBroker: false, canLogoutBroker: false });
    // Initialize account checkboxes (all unchecked for traders)
    setCreateAccounts(availableAccounts.map(a => ({ accountId: a.id, label: a.label, broker: a.broker, canTrade: false })));
  }

  // ─── Create user (Guardrail 1: traders need accounts) ───
  async function handleCreate() {
    setCreateError('');

    // Guardrail 1: Frontend check — traders must select at least 1 trade account
    if (form.role === 'trader') {
      const tradeAccounts = createAccounts.filter(a => a.canTrade);
      if (tradeAccounts.length === 0) {
        setCreateError('Traders must have at least 1 trade account selected');
        return;
      }
    }

    try {
      const payload = { ...form };
      // For traders, send selected accounts; for admin/viewer, backend auto-assigns
      if (form.role === 'trader') {
        payload.accounts = createAccounts.filter(a => a.canTrade).map(a => ({ accountId: a.accountId, canTrade: true }));
      }

      const res = await axios.post(`${API}/users`, payload);
      setSuccessMsg(`User "${form.username}" created. Temporary password: ${res.data.tempPassword}`);
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create user');
    }
  }

  // ─── Update user ────────────────────────────────────────
  async function handleUpdate() {
    try {
      await axios.put(`${API}/users/${editingUser.id}`, editForm);
      setSuccessMsg(`User "${editingUser.username}" updated`);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  }

  // ─── Delete user ────────────────────────────────────────
  function confirmDelete(user) {
    setConfirm({
      title: 'Delete User Permanently?',
      message: `Are you sure you want to permanently delete "${user.displayName}" (${user.username})? This will remove their account, all sessions, and all access. This action CANNOT be undone.`,
      confirmLabel: 'Yes, Delete Permanently',
      confirmColor: T.red,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.delete(`${API}/users/${user.id}`);
          setSuccessMsg(`User "${user.username}" permanently deleted`);
          fetchUsers();
        } catch (err) { setError(err.response?.data?.error || 'Failed to delete user'); }
      }
    });
  }

  // ─── Toggle active ──────────────────────────────────────
  function confirmToggleActive(user) {
    const action = user.isActive ? 'disable' : 'enable';
    const warning = user.isActive
      ? `Are you sure you want to DISABLE "${user.displayName}"? They will be immediately logged out and unable to log back in until re-enabled.`
      : `Re-enable "${user.displayName}"? They will be able to log in again.`;
    setConfirm({
      title: `${user.isActive ? 'Disable' : 'Enable'} User?`,
      message: warning,
      confirmLabel: user.isActive ? 'Yes, Disable User' : 'Yes, Enable User',
      confirmColor: user.isActive ? T.red : T.green,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.post(`${API}/users/${user.id}/toggle-active`);
          setSuccessMsg(`User "${user.username}" ${action}d`);
          fetchUsers();
        } catch (err) { setError(err.response?.data?.error || `Failed to ${action} user`); }
      }
    });
  }

  // ─── Kick (force logout) ────────────────────────────────
  function confirmKick(user) {
    setConfirm({
      title: 'Force Logout?',
      message: `Are you sure you want to force-logout "${user.displayName}"? All their active sessions (${user.activeSessions}) will be terminated immediately.`,
      confirmLabel: 'Yes, Force Logout',
      confirmColor: T.orange,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await axios.post(`${API}/users/${user.id}/kick`);
          setSuccessMsg(`"${user.username}" logged out (${res.data.sessionsKilled} sessions killed)`);
          fetchUsers();
        } catch (err) { setError(err.response?.data?.error || 'Failed to kick user'); }
      }
    });
  }

  // ─── Reset password — also uses random password now ────
  async function handleResetPassword(user) {
    setConfirm({
      title: 'Reset Password?',
      message: `Are you sure you want to reset the password for "${user.displayName}"? They will be logged out and must set a new password on next login.`,
      confirmLabel: 'Yes, Reset Password',
      confirmColor: T.orange,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await axios.post(`${API}/users/${user.id}/reset-password`, {});
          setSuccessMsg(`Password reset for "${user.username}". Temporary password: ${res.data.tempPassword}`);
          fetchUsers();
        } catch (err) { setError(err.response?.data?.error || 'Failed to reset password'); }
      }
    });
  }

  // ─── Reset TOTP ─────────────────────────────────────────
  function confirmResetTotp(user) {
    setConfirm({
      title: 'Reset Two-Factor Authentication?',
      message: `Are you sure you want to reset 2FA for "${user.displayName}"? They will be logged out and must set up their authenticator app again on next login.`,
      confirmLabel: 'Yes, Reset 2FA',
      confirmColor: T.orange,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.post(`${API}/users/${user.id}/reset-totp`);
          setSuccessMsg(`2FA reset for "${user.username}". They must set it up again on next login.`);
          fetchUsers();
        } catch (err) { setError(err.response?.data?.error || 'Failed to reset TOTP'); }
      }
    });
  }

  // ─── Unlock user (Guardrail 4) ──────────────────────────
  async function handleUnlock(user) {
    try {
      await axios.post(`${API}/users/${user.id}/unlock`);
      setSuccessMsg(`"${user.username}" has been unlocked`);
      fetchUsers();
    } catch (err) { setError(err.response?.data?.error || 'Failed to unlock user'); }
  }

  // ─── Account access — traders only (Guardrail 5) ────────
  function openAccountAccess(user) {
    const state = availableAccounts.map(acct => {
      const existing = user.accountAccess.find(a => a.accountId === acct.id);
      return { accountId: acct.id, label: acct.label, broker: acct.broker, canTrade: existing ? existing.canTrade : false };
    });
    setAccessUser(user);
    setAccessState(state);
  }

  async function saveAccountAccess() {
    // Guardrail 1 enforcement: traders must keep at least 1 trade account
    if (accessUser.role === 'trader') {
      const tradeCount = accessState.filter(a => a.canTrade).length;
      if (tradeCount === 0) {
        setError('Traders must have at least 1 trade account');
        return;
      }
    }
    const accounts = accessState.filter(a => a.canTrade).map(a => ({ accountId: a.accountId, canTrade: true }));
    try {
      await axios.post(`${API}/users/${accessUser.id}/account-access`, { accounts });
      setSuccessMsg(`Account access updated for "${accessUser.username}"`);
      setAccessUser(null);
      fetchUsers();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update account access'); }
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  if (loading) {
    return <div style={{ padding: 40, color: T.muted, fontSize: 13, textAlign: 'center' }}>Loading users...</div>;
  }

  const roleBadge = (role) => {
    const colors = { admin: { bg: '#1e3a5f', text: '#60a5fa' }, trader: { bg: '#1a3a1a', text: '#4ade80' }, viewer: { bg: '#3a3a1a', text: '#fbbf24' } };
    const c = colors[role] || colors.viewer;
    return <span style={{ padding: '2px 6px', fontSize: 9, fontWeight: 600, borderRadius: 3, background: c.bg, color: c.text }}>{role.toUpperCase()}</span>;
  };

  // Guardrail 5: Account display per role
  const accountDisplay = (user) => {
    if (user.role === 'admin') return <span style={{ color: T.blue, fontSize: 10 }}>All (full access)</span>;
    if (user.role === 'viewer') return <span style={{ color: T.yellow, fontSize: 10 }}>All (view only)</span>;
    // Trader — show which accounts they can trade on
    const tradeAccounts = user.accountAccess.filter(a => a.canTrade);
    if (tradeAccounts.length === 0) return <span style={{ color: T.red, fontSize: 10 }}>No accounts</span>;
    return tradeAccounts.map(a => <div key={a.accountId} style={{ color: T.text, fontSize: 10 }}>{a.accountId}</div>);
  };

  return (
    <div style={{ padding: 20, color: T.text, fontFamily: "'Segoe UI', sans-serif", overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>User Management</div>
          <div style={{ fontSize: 11, color: T.muted }}>{users.length} user{users.length !== 1 ? 's' : ''} registered</div>
        </div>
        <button onClick={openCreate} style={S.btnPrimary}>+ Create User</button>
      </div>

      {/* Messages */}
      {error && <div style={{ padding: '8px 12px', marginBottom: 12, fontSize: 12, background: '#2a1515', color: T.red, borderRadius: 6, border: '1px solid #4a2020' }}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: T.red, cursor: 'pointer', fontSize: 11 }}>✕</button></div>}
      {successMsg && <div style={{ padding: '8px 12px', marginBottom: 12, fontSize: 12, background: '#152a15', color: T.green, borderRadius: 6, border: '1px solid #204a20' }}>{successMsg}</div>}

      {/* Users table */}
      <div style={{ background: T.card, borderRadius: 8, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: T.headerBg }}>
              {['User', 'Role', 'Status', '2FA', 'Broker Perms', 'Accounts', 'Sessions', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: T.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${T.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${T.border}`, opacity: u.isActive ? 1 : 0.5 }}>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, color: T.text }}>{u.displayName}</div>
                  <div style={{ fontSize: 10, color: T.dim }}>@{u.username}</div>
                </td>
                <td style={{ padding: '10px 12px' }}>{roleBadge(u.role)}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ fontSize: 11, color: u.isActive ? T.green : T.red, fontWeight: 500 }}>
                    {u.isActive ? 'Active' : 'Disabled'}
                  </span>
                  {u.mustChangePassword && <div style={{ fontSize: 9, color: T.yellow }}>Must change password</div>}
                  {u.isLockedOut && <div style={{ fontSize: 9, color: T.red, fontWeight: 600 }}>🔒 LOCKED</div>}
                  {!u.isLockedOut && u.failedLoginCount > 0 && <div style={{ fontSize: 9, color: T.orange }}>{u.failedLoginCount} failed attempt{u.failedLoginCount !== 1 ? 's' : ''}</div>}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ fontSize: 11, color: u.totpEnabled ? T.green : T.yellow }}>
                    {u.totpEnabled ? 'Enabled' : 'Not set up'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', fontSize: 10 }}>
                  <div style={{ color: u.canLoginBroker ? T.green : T.dim }}>{u.canLoginBroker ? '✓' : '✗'} Login</div>
                  <div style={{ color: u.canLogoutBroker ? T.green : T.dim }}>{u.canLogoutBroker ? '✓' : '✗'} Logout</div>
                </td>
                {/* Guardrail 5: Simplified account display */}
                <td style={{ padding: '10px 12px', fontSize: 10 }}>{accountDisplay(u)}</td>
                <td style={{ padding: '10px 12px', fontSize: 11, color: u.activeSessions > 0 ? T.green : T.dim }}>
                  {u.activeSessions}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <button onClick={() => { setEditingUser(u); setEditForm({ displayName: u.displayName, role: u.role, canLoginBroker: u.canLoginBroker, canLogoutBroker: u.canLogoutBroker }); }} style={S.btnSmall}>Edit</button>
                    {/* Guardrail 5: Only traders get the Accounts button */}
                    {u.role === 'trader' && <button onClick={() => openAccountAccess(u)} style={S.btnSmall}>Accounts</button>}
                    <button onClick={() => handleResetPassword(u)} style={S.btnSmall}>Reset PW</button>
                    {u.totpEnabled && <button onClick={() => confirmResetTotp(u)} style={S.btnSmall}>Reset 2FA</button>}
                    {u.isLockedOut && <button onClick={() => handleUnlock(u)} style={{ ...S.btnSmall, color: T.blue, borderColor: '#1e3a5f' }}>Unlock</button>}
                    {u.activeSessions > 0 && <button onClick={() => confirmKick(u)} style={{ ...S.btnSmall, color: T.orange, borderColor: '#4a3020' }}>Kick</button>}
                    <button onClick={() => confirmToggleActive(u)} style={{ ...S.btnSmall, color: u.isActive ? T.yellow : T.green, borderColor: u.isActive ? '#4a4020' : '#204a20' }}>{u.isActive ? 'Disable' : 'Enable'}</button>
                    <button onClick={() => confirmDelete(u)} style={S.btnDanger}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ CREATE USER POPUP (Guardrails 1+3) ═══ */}
      {showCreate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowCreate(false)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, width: 440, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>Create New User</div>
            {createError && <div style={{ padding: '6px 10px', marginBottom: 12, fontSize: 11, background: '#2a1515', color: T.red, borderRadius: 4 }}>{createError}</div>}

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Username (lowercase, no spaces)</label>
              <input style={S.input} value={form.username} onChange={e => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} placeholder="e.g. rahul" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Display Name</label>
              <input style={S.input} value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} placeholder="e.g. Rahul Sharma" />
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 12, padding: '6px 10px', background: T.input, borderRadius: 4 }}>
              A random temporary password will be generated. The user must change it on first login.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Role</label>
              <select style={{ ...S.input, cursor: 'pointer' }} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="trader">Trader — can view + place trades</option>
                <option value="viewer">Viewer — can only view data</option>
                <option value="admin">Admin — full access</option>
              </select>
            </div>

            {/* Guardrail 1: Account selection for traders */}
            {form.role === 'trader' && (
              <div style={{ marginBottom: 12 }}>
                <label style={S.label}>Trade Accounts (select at least 1)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {createAccounts.map((acct, idx) => (
                    <label key={acct.accountId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: T.input, borderRadius: 5, border: `1px solid ${T.inputBorder}`, cursor: 'pointer', fontSize: 12, color: T.text }}>
                      <input type="checkbox" checked={acct.canTrade} onChange={e => {
                        const next = [...createAccounts];
                        next[idx] = { ...acct, canTrade: e.target.checked };
                        setCreateAccounts(next);
                      }} />
                      <span style={{ fontWeight: 600 }}>{acct.label}</span>
                      <span style={{ fontSize: 10, color: T.dim }}>({acct.accountId})</span>
                    </label>
                  ))}
                </div>
                {createAccounts.filter(a => a.canTrade).length === 0 && (
                  <div style={{ fontSize: 10, color: T.red, marginTop: 4 }}>At least 1 account must be selected</div>
                )}
              </div>
            )}

            {form.role === 'admin' && <div style={{ fontSize: 11, color: T.blue, marginBottom: 12, padding: '6px 10px', background: '#0f1e3a', borderRadius: 4 }}>Admins automatically get full access to all broker accounts.</div>}
            {form.role === 'viewer' && <div style={{ fontSize: 11, color: T.yellow, marginBottom: 12, padding: '6px 10px', background: '#2a2510', borderRadius: 4 }}>Viewers automatically get view-only access to all broker accounts.</div>}

            <div style={{ marginBottom: 12, display: 'flex', gap: 16 }}>
              <label style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.canLoginBroker} onChange={e => setForm({ ...form, canLoginBroker: e.target.checked })} /> Can login broker
              </label>
              <label style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.canLogoutBroker} onChange={e => setForm({ ...form, canLogoutBroker: e.target.checked })} /> Can logout broker
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ ...S.btnSmall, padding: '8px 20px', fontSize: 12 }}>Cancel</button>
              <button
                onClick={handleCreate}
                disabled={!form.username || !form.displayName || (form.role === 'trader' && createAccounts.filter(a => a.canTrade).length === 0)}
                style={{ ...S.btnPrimary, opacity: (!form.username || !form.displayName || (form.role === 'trader' && createAccounts.filter(a => a.canTrade).length === 0)) ? 0.5 : 1 }}
              >Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ EDIT USER POPUP ═══ */}
      {editingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEditingUser(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 4 }}>Edit User</div>
            <div style={{ fontSize: 11, color: T.dim, marginBottom: 16 }}>@{editingUser.username}</div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Display Name</label>
              <input style={S.input} value={editForm.displayName || ''} onChange={e => setEditForm({ ...editForm, displayName: e.target.value })} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Role</label>
              <select style={{ ...S.input, cursor: 'pointer' }} value={editForm.role || ''} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                <option value="trader">Trader</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ marginBottom: 12, display: 'flex', gap: 16 }}>
              <label style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={editForm.canLoginBroker || false} onChange={e => setEditForm({ ...editForm, canLoginBroker: e.target.checked })} /> Can login broker
              </label>
              <label style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={editForm.canLogoutBroker || false} onChange={e => setEditForm({ ...editForm, canLogoutBroker: e.target.checked })} /> Can logout broker
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setEditingUser(null)} style={{ ...S.btnSmall, padding: '8px 20px', fontSize: 12 }}>Cancel</button>
              <button onClick={handleUpdate} style={S.btnPrimary}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ACCOUNT ACCESS POPUP — Traders only (Guardrail 5) ═══ */}
      {accessUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setAccessUser(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 4 }}>Trade Accounts</div>
            <div style={{ fontSize: 11, color: T.dim, marginBottom: 16 }}>For: {accessUser.displayName} (@{accessUser.username})</div>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 12 }}>Select which accounts this trader can place orders on:</div>

            {accessState.length === 0 ? (
              <div style={{ fontSize: 12, color: T.muted, padding: 16, textAlign: 'center' }}>No broker accounts configured.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {accessState.map((acct, idx) => (
                  <label key={acct.accountId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: T.input, borderRadius: 6, border: `1px solid ${T.inputBorder}`, cursor: 'pointer', fontSize: 12, color: T.text }}>
                    <input type="checkbox" checked={acct.canTrade} onChange={e => {
                      const next = [...accessState];
                      next[idx] = { ...acct, canTrade: e.target.checked };
                      setAccessState(next);
                    }} />
                    <span style={{ fontWeight: 600 }}>{acct.label}</span>
                    <span style={{ fontSize: 10, color: T.dim }}>({acct.accountId} · {acct.broker})</span>
                  </label>
                ))}
              </div>
            )}

            {accessState.filter(a => a.canTrade).length === 0 && accessState.length > 0 && (
              <div style={{ fontSize: 10, color: T.red, marginTop: 6 }}>At least 1 account must be selected for traders</div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setAccessUser(null)} style={{ ...S.btnSmall, padding: '8px 20px', fontSize: 12 }}>Cancel</button>
              <button
                onClick={saveAccountAccess}
                disabled={accessState.filter(a => a.canTrade).length === 0}
                style={{ ...S.btnPrimary, opacity: accessState.filter(a => a.canTrade).length === 0 ? 0.5 : 1 }}
              >Save Access</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CONFIRMATION POPUP ═══ */}
      {confirm && (
        <ConfirmPopup
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmColor={confirm.confirmColor}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
